import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight, CheckCircle2 } from "lucide-react";
import { useSiteContent } from "@/lib/hooks";
import { useConsent, setConsent } from "@/lib/consent";

const ABOUT_IMG = "https://images.unsplash.com/photo-1555955207-b96159c16808?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxnYXJkZW5pbmclMjBvdXRkb29ycyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3ODU0Nzk0NTd8MA&ixlib=rb-4.1.0&q=85";

const DEFAULT_MAP = "https://www.openstreetmap.org/export/embed.html?bbox=-2.35%2C53.40%2C-2.14%2C53.52&layer=mapnik&marker=53.4808%2C-2.2426";

function useMapSrc(query, enabled) {
  const [src, setSrc] = useState(DEFAULT_MAP);
  useEffect(() => {
    if (!enabled || !query) return;
    fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`)
      .then((r) => r.json())
      .then(([hit]) => {
        if (!hit) return;
        const [s, n, w, e] = hit.boundingbox;
        setSrc(`https://www.openstreetmap.org/export/embed.html?bbox=${w}%2C${s}%2C${e}%2C${n}&layer=mapnik&marker=${hit.lat}%2C${hit.lon}`);
      })
      .catch(() => {});
  }, [query, enabled]);
  return src;
}

export default function About() {
  const { data: c } = useSiteContent();
  const consent = useConsent();
  const mapSrc = useMapSrc(c?.map_query || c?.service_area, consent === "accepted");

  return (
    <div data-testid="about-page">
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-green-700 font-bold uppercase tracking-widest text-xs">About us</span>
            <h1 className="font-display font-black text-slate-900 text-4xl sm:text-5xl mt-4 tracking-tight leading-[1.05]">
              {c?.company_name || "Speedex International"}
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">{c?.about_text}</p>
            <ul className="mt-6 space-y-3">
              {["Fully insured & professional", "Local, reliable & punctual", "Free no-obligation quotes"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-slate-700 font-medium">
                  <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" /> {t}
                </li>
              ))}
            </ul>
            <Link to="/quote" className="mt-8 inline-flex items-center gap-2 rounded-full bg-green-600 px-7 py-3.5 text-sm font-bold text-white hover:bg-green-700 transition-colors" data-testid="about-quote-btn">
              Get a Free Quote <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-green-600/10 rounded-3xl -z-10" />
            <img src={ABOUT_IMG} alt="Professional gardener at work" className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]" />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 md:py-20 grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-display font-extrabold text-slate-900 text-3xl tracking-tight">Get in touch</h2>
          <p className="text-slate-500 mt-3">Prefer to talk? Give us a call or drop us an email.</p>
          <div className="mt-8 space-y-4">
            {c?.contact_phone && (
              <a href={`tel:${c.contact_phone}`} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 hover:border-green-400 hover:shadow-md transition" data-testid="about-call">
                <div className="h-12 w-12 rounded-xl bg-green-50 text-green-600 grid place-items-center"><Phone className="h-6 w-6" /></div>
                <div><div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Call us</div><div className="font-bold text-slate-900">{c.contact_phone}</div></div>
              </a>
            )}
            {c?.contact_email && (
              <a href={`mailto:${c.contact_email}`} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 hover:border-green-400 hover:shadow-md transition" data-testid="about-email">
                <div className="h-12 w-12 rounded-xl bg-green-50 text-green-600 grid place-items-center"><Mail className="h-6 w-6" /></div>
                <div><div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Email us</div><div className="font-bold text-slate-900 break-all">{c.contact_email}</div></div>
              </a>
            )}
            {c?.contact_address && (
              <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5">
                <div className="h-12 w-12 rounded-xl bg-green-50 text-green-600 grid place-items-center"><MapPin className="h-6 w-6" /></div>
                <div><div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Find us</div><div className="font-bold text-slate-900">{c.contact_address}</div></div>
              </div>
            )}
          </div>
          {c?.service_area && (
            <div className="mt-6 rounded-2xl bg-green-50 border border-green-100 p-5">
              <div className="text-xs uppercase tracking-wider text-green-700 font-bold">Service area</div>
              <p className="text-slate-700 mt-1 font-medium">{c.service_area}</p>
            </div>
          )}
        </div>

        <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm min-h-[400px]">
          {consent === "accepted" ? (
            <iframe
              title="Service area map"
              src={mapSrc}
              className="w-full h-full min-h-[400px]"
              loading="lazy"
              data-testid="about-map"
            />
          ) : (
            <div className="h-full min-h-[400px] grid place-items-center bg-slate-100 p-8 text-center" data-testid="about-map-placeholder">
              <div>
                <MapPin className="h-8 w-8 text-green-600 mx-auto" />
                <p className="mt-3 text-slate-600 text-sm max-w-xs">The map is provided by OpenStreetMap and may set cookies.</p>
                <button onClick={() => setConsent("accepted")} className="mt-4 rounded-full bg-green-600 px-6 py-2.5 text-sm font-bold text-white hover:bg-green-700 transition-colors" data-testid="about-map-enable-btn">Accept cookies & show map</button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
